import { Typography } from "antd"
import "./privacy.scss";
import { useEffect } from "react";

const { Text, Paragraph, Title } = Typography

export const Privacy = () => {
  useEffect(() => {
    document.title = 'Instalab | Privacy Policy'
  }, [])

  return <>
    <div className="privacy">
      <Text className="privacy-header">
        INSTALAB PRIVACY POLICY
      </Text>
      <Text className="privacy-subheader">
        Effective Date: June 23, 2024
      </Text>
      <Text className="privacy-subheader">
        Last Updated Date: June 23, 2024
      </Text>

      <Paragraph className="privacy-paragraph">
        This Privacy Policy ("Policy") describes how Instalab, Inc. ("Instalab," "we," "us," or "our") collects, uses, shares, and protects your personal information. It also explains your privacy rights and how to exercise them. This Policy applies when you use our website (www.instalab.com), mobile apps, or any other services we provide (collectively, the "Services").
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        This Policy is designed to provide transparency and clarity regarding our data practices and also describes the choices and rights available to you with respect to your Personal Information. It further outlines the means by which you can exercise those rights and contact us with inquiries or concerns. By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by the practices and procedures described in this Policy. If you do not agree with any portion of this Policy, please do not use our Services.
      </Paragraph>

      <Title level={3} className="privacy-title">1. Definitions</Title>
      <Paragraph className="privacy-paragraph">
        "Personal Information" means information that identifies, relates to, describes, is reasonably capable of being associated with, or could reasonably be linked, directly or indirectly, with a particular individual or household. This includes but is not limited to identifiers such as your name, email address, phone number, device information, and online activity.
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        "Consumer Health Data" means any form of Personal Information that is linked or reasonably linkable to an individual and that identifies that individual's past, present, or anticipated physical or mental health status, condition, or behavior, including but not limited to health measurements, diagnoses, testing data, symptoms, treatment history, and physiological responses.
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        "Self-Reported Health Information" refers specifically to Consumer Health Data that you directly provide to us voluntarily. This may occur through forms you complete on our platform, integrations with wearable devices or health apps, or other user-driven disclosures.
      </Paragraph>

      <Title level={3} className="privacy-title">2. Scope and Applicability</Title>
      <Paragraph className="privacy-paragraph">
        This Privacy Policy applies to all Personal Information that we collect, access, use, disclose, or otherwise process in the course of providing our Services. This includes, without limitation:
      </Paragraph>
      <ul>
        <li>Information you provide directly to us via our Site, mobile application, surveys, customer service communications, or other user interfaces;</li>
        <li>Information we collect passively through automated technologies such as cookies, pixels, beacons, and device analytics;</li>
        <li>Information received from third-party service providers, including laboratory testing facilities, healthcare providers, wearable device manufacturers, data enrichment partners, and marketing platforms, where such third parties have a lawful basis to share data with us.</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        This Policy does not govern the data practices of entities or services that are not owned or controlled by Instalab. For example, when you access third-party websites, applications, or tools via links on our Site or as part of our Services, those third parties may collect information independently and their own privacy policies will apply. We are not responsible for and do not endorse the privacy practices of third parties.
      </Paragraph>

      <Title level={3} className="privacy-title">3. Categories of Personal Information We Collect</Title>
      <Paragraph className="privacy-paragraph">
        We collect and process various types of Personal Information, which includes a specialized subset referred to as Consumer Health Data. While Consumer Health Data falls under the broader umbrella of Personal Information, it is subject to distinct legal protections and consent requirements under applicable state and federal privacy laws.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">A. General Categories of Personal Information</Title>
      <Paragraph className="privacy-paragraph">
        This includes, but is not limited to:
      </Paragraph>
      <ul>
        <li>Identifiers: Name, email address, username, IP address, device ID, and similar unique identifiers.</li>
        <li>Commercial Information: Purchase history, service preferences, and responses to promotions or surveys.</li>
        <li>Financial Information: Credit card details (processed through secure third-party platforms), billing addresses, and transaction metadata.</li>
        <li>Geolocation Data: Information derived from your IP address or mobile device, ranging from general region to precise GPS coordinates.</li>
        <li>Audio/Visual Content: Voice recordings, video submissions, and photos voluntarily provided through customer interactions or service use.</li>
        <li>Inferences: Profiles, predictions, or derived data used to analyze or anticipate behaviors, preferences, or interests.</li>
      </ul>

      <Title level={4} className="privacy-subtitle">B. Consumer Health Data</Title>
      <Paragraph className="privacy-paragraph">
        Consumer Health Data is a subcategory of Personal Information and includes any data that identifies or is reasonably capable of being associated with your past, present, or future physical or mental health status. Examples include:
      </Paragraph>
      <ul>
        <li>Lab Results and Diagnostic Data received from third-party healthcare or laboratory providers.</li>
        <li>Self-Reported Health Information submitted through health assessments, symptom checkers, or intake forms.</li>
        <li>Biometric or Physiological Data collected from connected health devices, such as heart rate, sleep patterns, and blood oxygen levels.</li>
        <li>Health History and Risk Indicators such as medication use, chronic conditions, or reproductive health information.</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        Because of its sensitive nature, we handle Consumer Health Data with additional care, and only use or disclose it with your explicit consent.
      </Paragraph>

      <Title level={3} className="privacy-title">4. Sources of Personal Information</Title>
      <Paragraph className="privacy-paragraph">
        We obtain Personal Information from the following types of sources:
      </Paragraph>
      <ul>
        <li>Direct Interactions with You: Information you provide via account creation, forms, surveys, and health assessments.</li>
        <li>Automated Tracking Technologies: Cookies, pixels, beacons, and other digital tools used to monitor engagement and device characteristics.</li>
        <li>Linked Wearables and Health Apps: Devices and platforms (e.g., fitness trackers, smartwatches) that you authorize to transmit health metrics or activity data.</li>
        <li>Healthcare and Laboratory Partners: Authorized third parties that provide lab reports, test results, and clinical interpretations.</li>
        <li>Third-Party Services: Entities such as identity verification tools, marketing partners, or analytics platforms that contribute supplementary Personal Information.</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        We do not collect or share Consumer Health Data from third-party sources without your consent. Any integration with external healthcare entities is subject to applicable privacy laws and informed authorization.
      </Paragraph>

      <Title level={3} className="privacy-title">5. Use of Personal Information</Title>
      <Paragraph className="privacy-paragraph">
        We use Personal Information to provide and improve our Services. We distinguish between uses of general Personal Information and those involving Consumer Health Data.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">A. Use of General Personal Information</Title>
      <ul>
        <li>To operate and maintain the functionality of the Site and mobile app.</li>
        <li>To personalize and improve your experience based on usage patterns.</li>
        <li>To communicate with you, including sending notifications, updates, and promotional content.</li>
        <li>To perform internal analytics, develop new features, and optimize business operations.</li>
        <li>To detect, investigate, and prevent fraud, security incidents, and service abuse.</li>
        <li>For advertising and marketing purposes, including tailored messaging (subject to your opt-out rights).</li>
      </ul>

      <Title level={4} className="privacy-subtitle">B. Use of Consumer Health Data</Title>
      <ul>
        <li>To deliver core health-related Services, such as displaying lab results or generating health reports.</li>
        <li>To coordinate with authorized laboratories, providers, or devices.</li>
        <li>To maintain accuracy and quality in diagnostic or health-related outputs.</li>
        <li>To fulfill regulatory obligations and respond to lawful health information requests.</li>
        <li>To support scientific research or development initiatives, only with your explicit consent.</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        We do not use Consumer Health Data for marketing, targeted advertising, or third-party commercial purposes without your prior, affirmative consent.
      </Paragraph>

      <Title level={3} className="privacy-title">6. Disclosure of Personal Information</Title>
      <Paragraph className="privacy-paragraph">
        We may disclose Personal Information, including Consumer Health Data, to authorized recipients, subject to your privacy choices and applicable legal constraints.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">A. Disclosures of Personal Information</Title>
      <Paragraph className="privacy-paragraph">
        We may disclose Personal Information:
      </Paragraph>
      <ul>
        <li>To Service Providers and Affiliates: For the purpose of supporting our operations, including hosting, analytics, communications, fulfillment, customer support, and internal business functions. These disclosures are governed by contractual agreements requiring data protection and limiting use to service-related purposes.</li>
        <li>To Advertising and Marketing Partners: For interest-based advertising and campaign measurement. We do not share or sell sensitive data in these contexts. You may opt out as described in the "Your Rights" section.</li>
        <li>To Legal Authorities: In response to lawful requests such as subpoenas, court orders, or legal investigations, and to enforce our rights or protect our property or safety.</li>
        <li>To the Public (If You Choose): When you post voluntarily in a public area of the Site or Services (e.g., forums, testimonials).</li>
      </ul>

      <Title level={4} className="privacy-subtitle">B. Disclosures of Consumer Health Data</Title>
      <ul>
        <li>To Healthcare Providers and Labs: With your consent, to perform testing, coordinate care, or provide diagnostic interpretations.</li>
        <li>To Health-Related Service Providers: For functions like report generation or wearable integrations, in accordance with contractual protections.</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        We do not sell Personal Information, including Consumer Health Data, for monetary compensation. In certain jurisdictions, some disclosures (e.g., cookie data for advertising) may be considered a "sale" or "share" under law. Consumer Health Data is excluded from such activities.
      </Paragraph>

      <Title level={3} className="privacy-title">7. Cookies and Tracking Technologies</Title>
      <Paragraph className="privacy-paragraph">
        We use cookies and similar tracking mechanisms to support functionality and measure performance. These tools help us:
      </Paragraph>
      <ul>
        <li>Remember your login state and preferences</li>
        <li>Understand engagement with features or content</li>
        <li>Provide targeted offers (where permitted)</li>
        <li>Analyze technical diagnostics and site behavior</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        We honor legally recognized browser signals, such as Global Privacy Control (GPC). Blocking or disabling certain cookies may affect your experience.
      </Paragraph>

      <Title level={3} className="privacy-title">8. Data Security</Title>
      <Paragraph className="privacy-paragraph">
        We employ administrative, technical, and physical safeguards designed to protect Personal Information, including Consumer Health Data, from unauthorized access, misuse, disclosure, loss, or alteration. These safeguards include:
      </Paragraph>
      <ul>
        <li>Data encryption in transit and at rest</li>
        <li>Role-based access controls and audit trails</li>
        <li>Intrusion detection systems and continuous monitoring</li>
        <li>Regular security testing, patching, and vulnerability assessments</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        While we strive to maintain strong protections, no system can be 100% secure. If a data breach affecting your information occurs, we will notify you as required by applicable laws.
      </Paragraph>

      <Title level={3} className="privacy-title">9. Data Retention</Title>
      <Paragraph className="privacy-paragraph">
        We retain Personal Information, including Consumer Health Data, only for as long as is reasonably necessary to fulfill the purposes for which it was collected, as outlined in this Privacy Policy. These purposes may include:
      </Paragraph>
      <ul>
        <li>Providing and maintaining the Services you have requested</li>
        <li>Complying with legal, regulatory, or contractual obligations</li>
        <li>Enforcing our terms and resolving disputes</li>
        <li>Conducting audits, fraud prevention, and security operations</li>
        <li>Supporting research and service improvements (where legally permitted)</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        Retention durations vary based on the type and sensitivity of the data, the nature of our relationship with you, and applicable legal requirements. When your data is no longer needed for any of these purposes, we securely delete or de-identify it in accordance with industry best practices and legal mandates.
      </Paragraph>

      <Title level={3} className="privacy-title">10. Children's Privacy</Title>
      <Paragraph className="privacy-paragraph">
        Our Services are intended solely for individuals aged 18 and older. We do not knowingly collect, solicit, or process Personal Information from anyone under the age of 18 without their parent's permission. If we become aware that we have inadvertently collected such information, we will take prompt steps to delete it from our systems.
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        If you are a parent or legal guardian and believe that your child under 18 has provided Personal Information to us, please contact us immediately at legal@instalab.com so we can investigate and address the issue.
      </Paragraph>

      <Title level={3} className="privacy-title">11. Your Rights and Choices</Title>
      <Paragraph className="privacy-paragraph">
        Depending on your jurisdiction, you may have certain rights under applicable privacy laws regarding your Personal Information, including any Consumer Health Data we collect and process. These rights may include, but are not limited to:
      </Paragraph>
      <ul>
        <li>Right to Access: You may request a copy of the Personal Information we hold about you.</li>
        <li>Right to Correct: You may request correction of any inaccurate or incomplete data.</li>
        <li>Right to Delete: You may request deletion of your Personal Information, subject to certain legal exceptions.</li>
        <li>Right to Opt Out: You may opt out of the sale or sharing of Personal Information, or the use of your data for targeted advertising, profiling, or marketing.</li>
        <li>Right to Limit Use of Sensitive Data: In jurisdictions where applicable, you may limit our use of sensitive Personal Information to only what is necessary for the Services.</li>
        <li>Right to Data Portability: You may request a portable copy of your Personal Information in a commonly used format.</li>
        <li>Right to Withdraw Consent: Where processing is based on your consent, you may withdraw that consent at any time.</li>
        <li>Right to Appeal: If we deny your privacy-related request, you may appeal our decision in accordance with the procedures outlined in state-specific sections.</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        To exercise any of these rights, please contact us at legal@instalab.com or visit our Contact page. We will respond to verifiable requests in accordance with applicable laws, typically within 30 days. If additional time is needed, we will notify you and explain the reason for the delay.
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        Note: We may need to verify your identity before processing your request. In some cases, certain rights may not apply due to legal exceptions or operational limitations. We will inform you of any such limitations in our response.
      </Paragraph>

      <Title level={3} className="privacy-title">12. California Privacy Notice</Title>
      <Paragraph className="privacy-paragraph">
        This section applies exclusively to California residents and is provided in accordance with the California Consumer Privacy Act of 2018 ("CCPA"), the California Privacy Rights Act of 2020 ("CPRA"), and other applicable California statutes and regulations. It supplements the broader Privacy Policy and addresses specific rights, data categories, and disclosures required under California law.
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        For the purposes of this section, "Personal Information" means information that identifies, relates to, describes, is reasonably capable of being associated with, or could reasonably be linked, directly or indirectly, with a particular California resident or household. It excludes Publicly Available Information, aggregated or de-identified data, and information protected by other sector-specific laws (e.g., HIPAA).
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        California residents who are visually impaired, require language accommodations, or need accessible formats to exercise their privacy rights may contact us at legal@instalab.com.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">A. Categories of Personal Information Collected</Title>
      <Paragraph className="privacy-paragraph">
        We may collect the following categories of Personal Information about California consumers:
      </Paragraph>
      <ul>
        <li>Identifiers (e.g., name, email address, IP address, device ID)</li>
        <li>Commercial Information (e.g., transaction history, preferences)</li>
        <li>Financial Information (e.g., payment details, billing data)</li>
        <li>Internet or Network Activity (e.g., browsing data, cookie usage)</li>
        <li>Geolocation Data (e.g., GPS-based or IP-based location)</li>
        <li>Professional or Employment Information (e.g., job title, employer)</li>
        <li>Audio/Visual Information (e.g., support calls, image uploads)</li>
        <li>Characteristics of Protected Classifications (as defined under CA or federal law)</li>
        <li>Inferences (e.g., profiles based on behavior or attributes)</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        Certain Personal Information we collect may qualify as Sensitive Personal Information under California law, including:
      </Paragraph>
      <ul>
        <li>Account login credentials</li>
        <li>Payment and financial access data</li>
        <li>Message content submitted through Services</li>
        <li>Consumer Health Data (as defined and described in prior sections)</li>
        <li>Precise geolocation data</li>
      </ul>

      <Title level={4} className="privacy-subtitle">B. How We Use Your Personal Information</Title>
      <Paragraph className="privacy-paragraph">
        We use Personal Information for the following purposes:
      </Paragraph>
      <ul>
        <li>To contact you and provide information</li>
        <li>To deliver customer service and support</li>
        <li>To verify identity, eligibility, and age as required by law</li>
        <li>To operate, maintain, and improve our Site and Services</li>
        <li>To facilitate account features and user interaction</li>
        <li>To conduct internal analytics and usage insights</li>
        <li>To market our Services and products directly to you</li>
        <li>To promote third-party offerings (with your consent)</li>
        <li>To run promotions and sweepstakes</li>
        <li>For internal operations, governance, and development</li>
        <li>To comply with legal, auditing, and compliance obligations</li>
        <li>To investigate fraud or enforce claims</li>
        <li>To protect our infrastructure and employee safety</li>
        <li>For targeted or cross-context behavioral advertising (with notice and opt-out rights)</li>
      </ul>

      <Title level={4} className="privacy-subtitle">C. Sources of Personal Information</Title>
      <Paragraph className="privacy-paragraph">
        We collect Personal Information from:
      </Paragraph>
      <ul>
        <li>You directly (e.g., forms, account creation, health surveys)</li>
        <li>Automated tracking via cookies and device identifiers</li>
        <li>Linked wearable devices or third-party integrations</li>
        <li>Lab and healthcare partners (with your consent)</li>
        <li>Third-party service providers and marketing partners</li>
        <li>Social media platforms and data enrichment vendors</li>
        <li>Public databases and offline interactions where applicable</li>
      </ul>

      <Title level={4} className="privacy-subtitle">D. Disclosure of Personal Information</Title>
      <Paragraph className="privacy-paragraph">
        We disclose Personal Information for business purposes to:
      </Paragraph>
      <ul>
        <li>Service providers and contractors</li>
        <li>Payment processors and fraud prevention vendors</li>
        <li>Advertising platforms and data processors</li>
        <li>Legal, regulatory, or government authorities</li>
        <li>Professional advisors and auditors</li>
        <li>Business transferees in connection with mergers or acquisitions</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        Under the CCPA/CPRA, a "sale" or "share" of Personal Information may include sharing data for cross-context behavioral advertising. While we do not sell Personal Information for monetary value, we may share identifiers, commercial data, and internet activity with ad tech providers in this context.
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        We do not sell or share your Lab Results or any Consumer Health Data without your explicit, affirmative consent.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">E. California Privacy Rights</Title>
      <Paragraph className="privacy-paragraph">
        If you are a California resident, you may have the following rights:
      </Paragraph>
      <ul>
        <li>Right to Know and Access: Request what Personal Information we have collected, used, disclosed, or sold in the past 12 months.</li>
        <li>Right to Correct: Request correction of inaccurate Personal Information we hold about you.</li>
        <li>Right to Delete: Request that we delete Personal Information, subject to certain exceptions.</li>
        <li>Right to Opt Out of Sale/Sharing: Request to opt out of the sale or sharing of your Personal Information, including via cookies.</li>
        <li>Right to Limit Use of Sensitive Information: Restrict our use of Sensitive Personal Information to only what is necessary to perform Services.</li>
        <li>Right to Non-Discrimination: You will not be treated differently for exercising your privacy rights.</li>
        <li>Right to Request Disclosure: Under California's "Shine the Light" law, request a list of categories of Personal Information disclosed to third parties for their own marketing purposes, if applicable.</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        To exercise these rights, email legal@instalab.com or use the links provided in our Your Privacy Choices section at the bottom of our website.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">F. Opt-Out of Sale and Sharing</Title>
      <Paragraph className="privacy-paragraph">
        You may opt out of sale/sharing by:
      </Paragraph>
      <ul>
        <li>Enabling a recognized opt-out signal, such as the Global Privacy Control (GPC) in your browser (see https://globalprivacycontrol.org/orgs)</li>
        <li>Adjusting cookie settings in our cookie banner</li>
        <li>Managing app tracking settings on your mobile device</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        If you are not logged into your account, your opt-out choice will be linked to your browser/device only. We do not knowingly sell or share the Personal Information of children under 18 without legally required opt-in authorization. If you believe your child has provided data in error, contact us at legal@instalab.com.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">G. Retention of Personal Information</Title>
      <Paragraph className="privacy-paragraph">
        We retain Personal Information only for as long as necessary for the purposes outlined in this Policy. Retention durations vary depending on the type of data, the nature of our relationship, and applicable legal requirements. Our retention criteria include:
      </Paragraph>
      <ul>
        <li>Duration of your account or service use</li>
        <li>Statutory or contractual record-keeping obligations</li>
        <li>Ongoing legal disputes, investigations, or risk management</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        We securely delete or de-identify data once it is no longer needed.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">H. Do Not Track</Title>
      <Paragraph className="privacy-paragraph">
        Some browsers transmit "Do Not Track" (DNT) signals. At this time, we do not respond to DNT signals. For more information, visit www.allaboutdnt.com.
      </Paragraph>

      <Title level={3} className="privacy-title">13. Nevada Privacy Notice</Title>
      <Paragraph className="privacy-paragraph">
        We do not sell Personal Information for monetary value as defined under Nevada Revised Statutes Chapter 603A. However, under Nevada Senate Bills 220 and 370, Nevada residents have the right to request that we not sell certain Personal Information in the future.
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        If you are a Nevada resident and wish to make such a request, please email legal@instalab.com and include your full name, email address, and physical address so that we can verify your Nevada residency and respond appropriately. Should we begin selling Personal Information after your request, we will make reasonable efforts to comply with your opt-out.
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        In addition, Nevada SB 370 grants residents specific rights regarding Consumer Health Data, including the right to understand how such data is collected, used, shared, and retained. For more information about our handling of Consumer Health Data, please read the previous relevant sections.
      </Paragraph>

      <Title level={3} className="privacy-title">14. Privacy Notice for Residents of Other U.S. States</Title>
      <Paragraph className="privacy-paragraph">
        This section provides additional information for residents of states with enacted comprehensive privacy laws, including: Colorado, Connecticut, Delaware (2025), Iowa (2025), Montana (October 2024), Nebraska (2025), New Hampshire (2025), New Jersey (January 2025), Oregon, Texas, Utah, and Virginia (collectively referred to as the "State Privacy Laws"). These provisions apply to the extent Instalab is subject to each respective law.
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        For the purposes of this section, "personal data" means any information that is linked or reasonably linkable to an identified or identifiable individual, excluding publicly available data and de-identified information.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">A. Our Personal Data Practices</Title>
      <Paragraph className="privacy-paragraph">
        The categories of personal data we collect, the purposes for which we use it, and the categories of third parties to whom we disclose it are fully described in:
      </Paragraph>
      <ul>
        <li>"Types of Personal Information We Collect"</li>
        <li>"Sources of Personal Information and Consumer Health Data"</li>
        <li>"Disclosure of Personal Information and Consumer Health Data"</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        We do not sell personal data to third parties for monetary consideration. However, we may engage in data disclosures via cookies and tracking tools for advertising purposes, which may be considered a "sale" or "sharing" under some state laws. We do not sell or share Lab Results or Self-Reported Health Information without your explicit consent.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">B. Your Privacy Rights</Title>
      <Paragraph className="privacy-paragraph">
        If you reside in a state covered by a State Privacy Law, you may have the following rights (subject to applicable legal exceptions):
      </Paragraph>
      <ul>
        <li>Right to Know and Access: Obtain a copy of the personal data we process about you.</li>
        <li>Right to Correct: Request corrections of inaccurate or outdated personal data.</li>
        <li>Right to Delete: Request deletion of personal data held by us and our processors.</li>
        <li>Right to Opt Out: Object to the sale of personal data, targeted advertising, and profiling in connection with significant decisions (e.g., eligibility or access to services).</li>
        <li>Right to Data Portability: Obtain your personal data in a portable, structured format.</li>
        <li>Right to Limit Use of Sensitive Data: Restrict processing of sensitive data to that which is necessary to deliver requested Services.</li>
        <li>Right to Appeal: Challenge decisions related to the denial of your privacy requests.</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        If you are a Connecticut, Colorado, Delaware, Montana, Nebraska, New Jersey, Oregon, Texas, or Virginia resident, we will not process your sensitive personal data or infer such data from other signals without your affirmative, express consent. Residents of Iowa and Utah have the right to opt out of such processing.
      </Paragraph>

      <Paragraph className="privacy-paragraph">
        To exercise any of your rights, please contact us at legal@instalab.com. We will verify your request and respond within the timeframe required by law, generally within 45 to 60 days.
      </Paragraph>

      <Title level={4} className="privacy-subtitle">C. Appeals Process by State</Title>
      <Paragraph className="privacy-paragraph">
        If we decline your request, you have the right to appeal our decision. Below are instructions for submitting an appeal and how to contact your state's Attorney General if you remain unsatisfied:
      </Paragraph>
      <ul>
        <li>Colorado: Appeal within 45 days. Contact the CO Attorney General at (720) 508-6000 or submit online.</li>
        <li>Connecticut: Appeal within 60 days. Contact the CT Attorney General at (860) 808-5420 or submit online.</li>
        <li>Delaware (2025): Appeal within 60 days. Contact the DE DOJ at (302) 683-8800 or submit online.</li>
        <li>Iowa (2025): Appeal within 60 days. Contact the IA Attorney General at (888) 777-4590 or submit online.</li>
        <li>Montana (October 2024): Appeal within 60 days. Contact the MT Attorney General at (406) 444-4500 or submit online.</li>
        <li>Nebraska (2025): Appeal within 60 days. Contact the NE Attorney General at (402) 471-2683 or submit online.</li>
        <li>New Hampshire (2025): Appeal within 60 days. Contact the NH Attorney General at (603) 271-3658 or submit online.</li>
        <li>New Jersey (January 2025): Appeal within 45 days. Contact the NJ Attorney General at (800) 242-5846 or submit online.</li>
        <li>Oregon: Appeal within 45 days. Contact the OR Attorney General at (877) 877-9392 or submit online.</li>
        <li>Texas: Appeal within 60 days. Contact the TX Attorney General at (800) 621-0508 or submit online.</li>
        <li>Virginia: Appeal within 60 days. Contact the VA Attorney General at (804) 786-2071 or submit online at oag.state.va.us.</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        If you are unsure of how your state's laws apply to you, we encourage you to reach out to us at legal@instalab.com for clarification or guidance.
      </Paragraph>

      <Title level={3} className="privacy-title">15. Changes to This Policy</Title>
      <Paragraph className="privacy-paragraph">
        We reserve the right to modify this Policy at any time. Changes will be effective upon posting the updated Policy on our Site. Your continued use of the Services constitutes acceptance of those changes.
      </Paragraph>

      <Title level={3} className="privacy-title">16. Contact Information</Title>
      <Paragraph className="privacy-paragraph">
        If you have any questions, comments, or concerns about this Privacy Policy, our data handling practices, your privacy rights, or if you would like to submit a request regarding your Personal Information, please contact us using the information below:
      </Paragraph>
      <ul>
        <li>Email: legal@instalab.com</li>
        <li>Mailing Address: 169 Madison Ave #2885, New York, NY 10016</li>
      </ul>

      <Paragraph className="privacy-paragraph">
        We strive to respond to all inquiries in a timely and thorough manner consistent with our legal obligations and internal standards.
      </Paragraph>
    </div>

  </>
}